import { useContext } from 'react';
import { UQL_PAGINATED } from '../constants';
import { DashboardContext } from './DashboardContext';
import {
  useQuery,
  useInfiniteQuery,
  UseQueryOptions,
  UseInfiniteQueryOptions,
  QueryFunctionContext,
} from '@tanstack/react-query';
import {
  DataSourceState,
  Datasources,
  QueryOptions,
  QueryResult,
  UQLResponseType,
  Registry,
  ProcessedQueryResults,
} from '../models/DatasourcesModels';
import { processDatasourcesQueryResults } from '../utils/processDatasourcesQueryResults';
import isEmpty from 'lodash/isEmpty';

const DEFAULT_RESPONSE: ProcessedQueryResults = {
  data: {},
  successes: {},
  loadings: {},
  errors: {},
  isLoading: false,
  hasError: false,
  statuses: {},
};

const isUQLPaginatedQuery = (q: QueryOptions): boolean => q.queryKey?.[0] === UQL_PAGINATED;

const getInfiniteQueryOptions = (q: QueryOptions): UseInfiniteQueryOptions =>
  ({ ...q, getNextPageParam: (r: UQLResponseType) => r?._links?.next?.href } as UseInfiniteQueryOptions);

const fetchQuery = (q: QueryOptions): QueryResult =>
  isUQLPaginatedQuery(q) ? useInfiniteQuery(getInfiniteQueryOptions(q)) : useQuery(q as UseQueryOptions);

const fetchQueries = (qo: QueryOptions[]): QueryResult[] =>
  qo.map((q: QueryOptions) => promoteNestedData(fetchQuery(q)) as QueryResult);

// kept existing logic
const promoteNestedData = (qr: QueryResult): unknown =>
  qr?.data != null && typeof qr.data === 'object' && !Array.isArray(qr.data) ? { ...qr, ...qr.data } : qr;

const toQueryOptions = (
  ds: DataSourceState,
  state: string | undefined,
  DatasourceRegistry: Readonly<Registry>,
): QueryOptions => ({
  queryKey: [ds.type ?? '', { ...(ds.options ?? {}), state }],
  queryFn: async ({ queryKey: [_key, options], pageParam }: QueryFunctionContext) =>
    await DatasourceRegistry[_key as string]({ ...(options as Record<string, unknown>), pageParam }),
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchInterval: ds.refetchInterval,
  enabled: ds.enabled ?? true,
});

export const useDatasources = (datasources: Datasources = {}, state: string | undefined): ProcessedQueryResults => {
  if (isEmpty(datasources)) {
    return DEFAULT_RESPONSE;
  }

  const { DatasourceRegistry } = useContext(DashboardContext);
  const datasourceQueryOptions = Object.values(datasources).map(value =>
    toQueryOptions(value, state, DatasourceRegistry),
  );
  const fetchedDatasourceResults = fetchQueries(datasourceQueryOptions);
  return processDatasourcesQueryResults(fetchedDatasourceResults, datasourceQueryOptions, datasources);
};
